import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllCourses,
  getSingleCourse,
  createCourse,
  updateCourse,
  deleteCourse,
} from "@/api/new-api/staff-management/menu-manage/special-menu/course";

// Thunks
export const fetchAllCourses = createAsyncThunk(
  "courses/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllCourses(restaurant_id);
    if (res.status === "success") return res.data.courses;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleCourse = createAsyncThunk(
  "courses/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleCourse(id);
    if (res.status === "success") return res.data.course;
    return rejectWithValue(res.message);
  }
);

export const createNewCourse = createAsyncThunk(
  "courses/create",
  async (data, { rejectWithValue }) => {
    const res = await createCourse(data);
    if (res.status === "success") return res.data.course;
    return rejectWithValue(res.message);
  }
);

export const updateExistingCourse = createAsyncThunk(
  "courses/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateCourse(id, data);
    if (res.status === "success") return res.data.course;
    return rejectWithValue(res.message);
  }
);

export const deleteCourseById = createAsyncThunk(
  "courses/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteCourse(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State
const initialState = {
  courses: [],
  singleCourse: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const courseSlice = createSlice({
  name: "courses",
  initialState,
  reducers: {
    clearCourseMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllCourses.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllCourses.fulfilled, (state, action) => {
        state.getLoading = false;
        state.courses = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllCourses.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleCourse.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleCourse.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleCourse = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleCourse.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewCourse.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewCourse.fulfilled, (state, action) => {
        state.createLoading = false;
        state.courses.push(action.payload);
        state.success = "Course created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewCourse.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingCourse.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingCourse.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.courses = state.courses.map((course) =>
          course.id === action.payload.id ? action.payload : course
        );
        state.success = "Course updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingCourse.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteCourseById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteCourseById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.courses = state.courses.filter(
          (course) => course.id !== action.payload
        );
        state.success = "Course deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteCourseById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearCourseMessages } = courseSlice.actions;
export default courseSlice.reducer;
